import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import mixAirplane from '../../../assets/mixAudio/mix-airplane.mp3';
import mixBird from '../../../assets/mixAudio/mix-bird.mp3';
import mixForest from '../../../assets/mixAudio/mix-forest.mp3';
import mixRain from '../../../assets/mixAudio/mix-rain.mp3';
import { ALL_MIX_SOUND_IDS, type MixSoundId } from './soundMixActiveSounds';

/** 지름 100px → 0.2, 400px → 1.0 선형 보간 */
export function mixVolumeFromDiameterPx(diameterPx: number): number {
  const d = Math.max(100, Math.min(400, diameterPx));
  return 0.2 + ((d - 100) / 300) * 0.8;
}

const MIX_DEFAULT_DIAMETER_PX = 180;

const MIX_AUDIO_FALLBACK_SRC: Partial<Record<MixSoundId, string>> = {
  rain: mixRain,
  bird: mixBird,
  forest: mixForest,
  flight: mixAirplane,
};

export type MixAudioPlaybackStatus = 'idle' | 'playing' | 'blocked';

function clamp01(v: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return 1;
  return Math.max(0, Math.min(1, v));
}

function volumeForDiameter(diameterPxById: Readonly<Record<MixSoundId, number>>, id: MixSoundId): number {
  const px = diameterPxById[id] ?? MIX_DEFAULT_DIAMETER_PX;
  return clamp01(mixVolumeFromDiameterPx(px));
}

/** 교차 출처면 `MediaElementSource`+`Gain` 불가 → `HTMLAudioElement.volume`만 (Safari 등에서 무시될 수 있음) */
function isCrossOriginMediaUrl(src: string): boolean {
  if (typeof window === 'undefined') return false;
  if (/^(data:|blob:)/i.test(src)) return false;
  try {
    const u = new URL(src, window.location.href);
    if (!/^https?:$/i.test(u.protocol)) return false;
    return u.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function createAudioContext(): AudioContext {
  const w = globalThis as typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };
  const Ctor = globalThis.AudioContext ?? w.webkitAudioContext;
  return new Ctor();
}

/**
 * 패드 지름으로 볼륨 제어.
 * - **동일 출처**(번들 mp3, dev에서 `/sounds` 프록시): `GainNode` — Safari/네이버 인앱에서도 지름별 음량 반영.
 * - **교차 출처 CDN 직링크**: `audio.volume`만 사용(브라우저가 무시할 수 있음).
 */
export function useMixAudioPlayback(
  activeSet: ReadonlySet<MixSoundId>,
  diameterPxById: Readonly<Record<MixSoundId, number>>,
  hostedSrcById: Readonly<Partial<Record<MixSoundId, string>>> | null,
) {
  const poolRef = useRef(new Map<MixSoundId, HTMLAudioElement>());
  const lastSrcByIdRef = useRef(new Map<MixSoundId, string>());
  const gainRef = useRef(new Map<MixSoundId, GainNode>());
  const useElementVolumeOnlyRef = useRef(new Map<MixSoundId, boolean>());
  const ctxRef = useRef<AudioContext | null>(null);
  const diameterRef = useRef(diameterPxById);
  diameterRef.current = diameterPxById;

  const [status, setStatus] = useState<MixAudioPlaybackStatus>('idle');

  const mergedSrc = useMemo((): Partial<Record<MixSoundId, string>> => {
    return { ...MIX_AUDIO_FALLBACK_SRC, ...(hostedSrcById ?? {}) };
  }, [hostedSrcById]);

  const audioSrcSig = useMemo(
    () => ALL_MIX_SOUND_IDS.map((id) => `${id}:${mergedSrc[id] ?? ''}`).join(','),
    [mergedSrc],
  );

  const activeKey = useMemo(() => [...activeSet].sort().join(','), [activeSet]);

  const diameterSig = useMemo(
    () => ALL_MIX_SOUND_IDS.map((id) => `${id}:${diameterPxById[id] ?? MIX_DEFAULT_DIAMETER_PX}`).join(','),
    [diameterPxById],
  );

  function getOrCreateAudio(pool: Map<MixSoundId, HTMLAudioElement>, id: MixSoundId, src: string | undefined) {
    if (!src) return null;
    const volumeOnly = isCrossOriginMediaUrl(src);
    let el: HTMLAudioElement | undefined = pool.get(id);
    const prev = lastSrcByIdRef.current.get(id);
    const prevVolOnly = useElementVolumeOnlyRef.current.get(id);

    if (el && prev !== undefined && prev !== src && prevVolOnly !== volumeOnly) {
      gainRef.current.delete(id);
      pool.delete(id);
      el = undefined;
    }

    if (!el) {
      el = new Audio();
      el.loop = true;
      el.preload = 'auto';
      el.removeAttribute('crossOrigin');
      el.src = src;
      pool.set(id, el);
      lastSrcByIdRef.current.set(id, src);
      useElementVolumeOnlyRef.current.set(id, volumeOnly);
      return el;
    }
    if (prev !== src) {
      el.pause();
      el.removeAttribute('crossOrigin');
      el.src = src;
      el.load();
      lastSrcByIdRef.current.set(id, src);
      useElementVolumeOnlyRef.current.set(id, volumeOnly);
    }
    return el;
  }

  function ensureGain(id: MixSoundId, el: HTMLAudioElement): GainNode | null {
    if (useElementVolumeOnlyRef.current.get(id)) return null;

    let g = gainRef.current.get(id);
    if (g) return g;

    let ctx = ctxRef.current;
    if (!ctx) {
      ctx = createAudioContext();
      ctxRef.current = ctx;
    }

    const source = ctx.createMediaElementSource(el);
    g = ctx.createGain();
    source.connect(g);
    g.connect(ctx.destination);
    el.volume = 1;
    gainRef.current.set(id, g);
    return g;
  }

  function applyVolume(id: MixSoundId, el: HTMLAudioElement, v: number) {
    const nv = clamp01(v);
    if (useElementVolumeOnlyRef.current.get(id)) {
      gainRef.current.delete(id);
      el.volume = nv;
      return;
    }
    const g = gainRef.current.get(id);
    if (g) {
      g.gain.value = nv;
    } else {
      el.volume = nv;
    }
  }

  useLayoutEffect(() => {
    const pool = poolRef.current;
    const dMap = diameterRef.current;
    for (const id of ALL_MIX_SOUND_IDS) {
      const src = mergedSrc[id];
      if (!src) continue;
      const el = getOrCreateAudio(pool, id, src);
      if (!el) continue;
      applyVolume(id, el, volumeForDiameter(dMap, id));
    }
  }, [diameterSig, audioSrcSig, mergedSrc]);

  useEffect(() => {
    const pool = poolRef.current;
    let cancelled = false;

    const run = async () => {
      let anyPlaying = false;
      let anyBlocked = false;

      const ctx = ctxRef.current;
      if (ctx?.state === 'suspended') {
        try {
          await ctx.resume();
        } catch {
          /* ignore */
        }
      }

      for (const id of ALL_MIX_SOUND_IDS) {
        const src = mergedSrc[id];
        if (!src) continue;

        const el = getOrCreateAudio(pool, id, src);
        if (!el) continue;
        const v = volumeForDiameter(diameterRef.current, id);

        if (activeSet.has(id)) {
          try {
            const gain = ensureGain(id, el);
            if (gain) {
              gain.gain.value = v;
            } else {
              el.volume = v;
            }
            await el.play();
            const v2 = volumeForDiameter(diameterRef.current, id);
            if (gain) {
              gain.gain.value = v2;
            } else {
              el.volume = v2;
            }
            anyPlaying = true;
          } catch {
            anyBlocked = true;
          }
        } else {
          el.pause();
        }
      }

      if (cancelled) return;
      if (anyBlocked) {
        setStatus('blocked');
      } else if (anyPlaying) {
        setStatus('playing');
      } else {
        setStatus('idle');
      }
    };

    void run();

    return () => {
      cancelled = true;
      for (const el of pool.values()) {
        el.pause();
      }
    };
  }, [activeKey, activeSet, audioSrcSig, mergedSrc]);

  const resumeFromUserGesture = useCallback(async () => {
    const pool = poolRef.current;
    let anyPlaying = false;
    let anyBlocked = false;

    const ctx = ctxRef.current;
    if (ctx?.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        /* ignore */
      }
    }

    for (const id of ALL_MIX_SOUND_IDS) {
      const src = mergedSrc[id];
      if (!src) continue;

      const el = getOrCreateAudio(pool, id, src);
      if (!el) continue;
      const v = volumeForDiameter(diameterRef.current, id);

      if (activeSet.has(id)) {
        try {
          const gain = ensureGain(id, el);
          if (gain) {
            gain.gain.value = v;
          } else {
            el.volume = v;
          }
          await el.play();
          const v2 = volumeForDiameter(diameterRef.current, id);
          if (gain) {
            gain.gain.value = v2;
          } else {
            el.volume = v2;
          }
          anyPlaying = true;
        } catch {
          anyBlocked = true;
        }
      } else {
        el.pause();
      }
    }

    if (anyBlocked) {
      setStatus('blocked');
    } else if (anyPlaying) {
      setStatus('playing');
    } else {
      setStatus('idle');
    }
  }, [activeSet, mergedSrc]);

  return { status, resumeFromUserGesture };
}
